import { createContext, useState, useEffect, useCallback } from "react";
import VendorServices from "../services/VendorServices";
import VendorHelper from "../helpers/VendorHelper";
import { EVENTS } from "../const";
import { eventsEmitter } from "utils/events";

const VendorContext = createContext();

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const [vendorJournals, setVendorJournals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleVendorUpdated = (updatedVendor) => {
      if (!updatedVendor) return;
      setVendors((prev) => {
        const idx = prev.findIndex(
          (v) =>
            (updatedVendor.local_id && v.local_id === updatedVendor.local_id) ||
            (updatedVendor.id && Number(v.id) === Number(updatedVendor.id)),
        );
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...updatedVendor };
          return next;
        }
        return [...prev, updatedVendor];
      });
    };

    const unsub = eventsEmitter.on(EVENTS.VENDOR_UPDATED, handleVendorUpdated);
    return unsub;
  }, []);

  const fetchVendors = useCallback(async (syncFromBE = false) => {
    setLoading(true);
    try {
      const { vendors: data } = await VendorHelper.loadInitialVendors(syncFromBE);
      setVendors(data);
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVendorJournals = useCallback(async (vendorId, params = {}) => {
    if (!vendorId) {
      setVendorJournals([]);
      return [];
    }
    try {
      const res = await VendorHelper.getVendorJournals(vendorId, params);
      const sorted = (res?.data || []).sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
      setVendorJournals(sorted);
      return sorted;
    } catch (err) {
      console.error("Failed to fetch vendor journals:", err);
      setVendorJournals([]);
      throw err;
    }
  }, []);

  const addVendor = async (data) => {
    try {
      const res = await VendorServices.createVendor(data);
      if (res) {
        await fetchVendors(true);
        return res;
      }
    } catch (err) {
      console.error("Failed to add vendor:", err);
      throw err;
    }
  };

  const updateVendor = async (id, data) => {
    try {
      const res = await VendorServices.updateVendor(id, data);
      if (res) {
        const updatedVendors = vendors.map((v) => (Number(v.id) === Number(id) ? { ...v, ...res } : v));
        setVendors(updatedVendors);
        const updatedVendor = updatedVendors.find((v) => Number(v.id) === Number(id));
        if (updatedVendor?.local_id) {
          await VendorHelper.updateVendorInDB(updatedVendor);
        }
        return res;
      }
    } catch (err) {
      console.error("Failed to update vendor:", err);
      throw err;
    }
  };

  const deleteVendor = async (id) => {
    try {
      const vendorToDelete = vendors.find((v) => Number(v.id) === Number(id));
      await VendorServices.deleteVendor(id);
      const filteredVendors = vendors.filter((v) => Number(v.id) !== Number(id));
      setVendors(filteredVendors);
      if (vendorToDelete?.local_id) {
        await VendorHelper.deleteVendorFromDB(vendorToDelete.local_id);
      }
    } catch (err) {
      console.error("Failed to delete vendor:", err);
      throw err;
    }
  };

  const addPaymentToVendor = async (id, data) => {
    try {
      const res = await VendorServices.addPaymentToVendor(id, data);
      await fetchVendors(true); // Refresh vendors and sync to DB
      return res;
    } catch (err) {
      console.error("Failed to add payment to vendor:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  return (
    <VendorContext.Provider
      value={{
        vendors,
        loading,
        fetchVendors,
        addVendor,
        updateVendor,
        deleteVendor,
        addPaymentToVendor,
        vendorJournals,
        setVendorJournals,
        fetchVendorJournals,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};

export default VendorContext;
